import i18n from '@/plugins/i18n';
import { isNegativ } from '@/helpers/helped-chart-functions.helper';
import { chartColors } from '@/utils/charts-colors';

export enum ESide {
  left = 'left',
  right = 'right'
}
export type CONTAINER = {
  sideLegend: {
    left: POSITION;
    right: POSITION;
  };
  middle: number;
};
export type POSITION = {
  startX: number;
  endX: number;
};

interface Payload {
  avg: number;
  indicator: 'good' | 'warning' | 'bad';
}

export class DrawAnglesFeetSVG {
  // Val
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly analysisType: string;
  private readonly type: string;

  private readonly angles: Record<ESide, Payload | undefined> = {
    left: undefined,
    right: undefined
  };

  private readonly pos: any = {
    legend: {
      startY: 0,
      endY: 0
    },
    middleX: 0,
    angles: {
      left: 0,
      right: 0
    }
  };

  private readonly angleName: any = {
    heelStrike: {
      walking: i18n.t('commons.standards.walk.heel-strike') as string,
      running: i18n.t('commons.standards.run.heel-strike') as string
    },
    flatFoot: {
      walking: i18n.t('commons.standards.walk.flat-foot') as string,
      running: i18n.t('commons.standards.run.flat-foot') as string
    },
    heelOff: {
      walking: i18n.t('commons.standards.walk.heel-off') as string,
      running: i18n.t('commons.standards.run.heel-off') as string
    },
    toesOff: {
      walking: i18n.t('commons.standards.walk.toe-off') as string,
      running: i18n.t('commons.standards.run.toe-off') as string
    }
  };

  private readonly phaseImg: any = {
    heelStrike: {
      link: require('@/assets/images/heel-strike-shoe.svg'),
      width: 60,
      height: 69
    },
    flatFoot: {
      link: require('@/assets/images/flat-foot-shoe.svg'),
      width: 80,
      height: 69
    },
    heelOff: {
      link: require('@/assets/images/heel-off-shoe.svg'),
      width: 54,
      height: 70.5
    },
    toesOff: {
      link: require('@/assets/images/toe-off-shoe.svg'),
      width: 50,
      height: 82
    }
  };

  constructor(canvas: HTMLCanvasElement, analysisType: string, type: string, left: Payload, right: Payload) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.analysisType = analysisType;
    this.type = type;

    // Define
    this.angles.left = left;
    this.angles.right = right;

    this.pos.legend.startY = this.canvas.height * 0.7; // get 70%
    this.pos.legend.endY = this.canvas.height;
    this.pos.middleX = this.canvas.width / 2;
    this.pos.angles.left = this.pos.middleX - this.pos.middleX / 2;
    this.pos.angles.right = this.pos.middleX + this.pos.middleX / 2;
  }

  async draw() {
    this.clean();
    //
    this.angleText();
    this.angleLine();
    this.contentStructure();
    this.angleImg();
  }

  // Structure
  contentStructure() {
    // Globals
    this.ctx.lineWidth = 1.5;
    this.ctx.strokeStyle = '#020C27';
    this.ctx.lineCap = 'round';
    const paddingAngulationText = 10;
    this.ctx.font = '500 12px Montserrat';
    this.canvas.style.letterSpacing = '1px';
    this.ctx.globalCompositeOperation = 'destination-over';

    // Horizontal bar
    this.ctx.beginPath();
    this.ctx.moveTo(0, this.pos.legend.startY);
    this.ctx.lineTo(this.canvas.width, this.pos.legend.startY);
    this.ctx.stroke();
    // Vertical bar
    this.ctx.beginPath();
    this.ctx.moveTo(this.pos.middleX, 25);
    this.ctx.lineTo(this.pos.middleX, this.pos.legend.startY);
    this.ctx.stroke();

    // Angulation text
    this.ctx.font = '500 13px Montserrat';
    this.ctx.textAlign = 'center';
    this.ctx.fillStyle = '#020C27';

    // text
    let currentText = this.angleName[this.type][this.analysisType];

    while (currentText.length > 0 && this.ctx.measureText(currentText).width > this.canvas.width) {
      if (!currentText.endsWith('...') && currentText.length >= 4) {
        currentText = currentText.slice(0, -4);
        currentText += '...';
      } else {
        currentText = currentText.slice(0, -1);
      }
    }

    this.ctx.fillText(currentText, this.pos.middleX, paddingAngulationText);
  }

  // Angles
  angleText() {
    // Globals
    const paddingText = 25;
    const valuesBackground = {
      height: 25,
      width: 55,
      yPadding: 7
    };
    //
    this.ctx.font = '500 14px Montserrat';
    this.ctx.textAlign = 'center';
    this.ctx.fillStyle = '#020C27';
    this.canvas.style.letterSpacing = '0px';

    for (const side of Object.values(ESide)) {
      let bgColor!: string;
      let color!: string;
      switch (this.angles[side]?.indicator) {
        case 'bad':
          color = chartColors.bad.fill;
          bgColor = chartColors.bad.background;
          break;
        case 'warning':
          color = chartColors.warning.fill;
          bgColor = chartColors.warning.background;
          break;
        case 'good':
          color = chartColors.good.fill;
          bgColor = chartColors.good.background;
          break;
        default:
          color = chartColors.neutral.fill;
          bgColor = chartColors.neutral.background;
      }

      this.ctx.fillStyle = bgColor;
      this.ctx.beginPath();
      this.ctx.rect(
        this.pos.angles[side] - valuesBackground.width / 2,
        this.pos.legend.startY + 20 - valuesBackground.height / 2,
        valuesBackground.width,
        valuesBackground.height
      );
      this.ctx.fill();

      // Text
      this.ctx.fillStyle = color;
      this.ctx.fillText(
        `${this.angles[side]!.avg} °`,
        this.pos.angles[side],
        this.pos.legend.startY + paddingText
      );
    }
  }

  angleLine() {
    // Globals
    const line = {
      size: 35,
      width: 2.5,
      marginBottom: 1.5
    };
    const leftAngle = Math.abs(this.angles.left!.avg);
    const rightAngle = Math.abs(this.angles.right!.avg);
    const angles: any = {
      left: {
        x: Math.cos((leftAngle * Math.PI) / 180) * line.size,
        y: Math.sin((leftAngle * Math.PI) / 180) * line.size
      },
      right: {
        x: Math.cos((rightAngle * Math.PI) / 180) * line.size,
        y: Math.sin((rightAngle * Math.PI) / 180) * line.size
      }
    };
    const lineX: any = {
      left: isNegativ(this.angles.left!.avg)
        ? this.pos.angles.left + angles.left.x
        : this.pos.angles.left - angles.left.x,
      right: isNegativ(this.angles.right!.avg)
        ? this.pos.angles.right - angles.right.x
        : this.pos.angles.right + angles.right.x
    };
    //
    this.ctx.lineCap = 'round';
    this.ctx.lineWidth = line.width;

    for (const side of ['left', 'right']) {
      const sideColor = side === 'left' ? '#ffbd1a' : '#2962fd';
      this.ctx.fillStyle = side === 'left' ? '#EFE9FF' : '#EBF4FD';
      this.ctx.strokeStyle = sideColor;
      // Angle shape
      this.ctx.beginPath();
      this.ctx.moveTo(this.pos.angles[side], this.pos.legend.startY - line.marginBottom);
      this.ctx.lineTo(lineX[side], this.pos.legend.startY - angles[side].y - line.marginBottom);
      this.ctx.lineTo(lineX[side], this.pos.legend.startY - line.marginBottom);
      this.ctx.fill();

      // Angle line
      this.ctx.beginPath();
      this.ctx.moveTo(this.pos.angles[side], this.pos.legend.startY - line.marginBottom);
      this.ctx.lineTo(lineX[side], this.pos.legend.startY - angles[side].y - line.marginBottom);
      this.ctx.stroke();

      // Baseline point
      this.ctx.beginPath();
      this.ctx.fillStyle = sideColor;
      this.ctx.arc(this.pos.angles[side], this.pos.legend.startY - line.marginBottom, 2, 0, 2 * Math.PI);
      this.ctx.fill();
    }
  }

  angleImg() {
    // GLobals
    const that: any = this;
    const resize = 0.6;
    const angleImages = {
      left: {
        img: new Image(),
        imgSrc: this.phaseImg[this.type].link
      },
      right: {
        img: new Image(),
        imgSrc: this.phaseImg[this.type].link
      },
      height: this.phaseImg[this.type].height,
      width: this.phaseImg[this.type].width
    };

    // resize images
    angleImages.height *= resize;
    angleImages.width *= resize;

    // left
    this.ctx.globalCompositeOperation = 'destination-over';

    angleImages.left.img.addEventListener('load', () => {
      that.ctx!.drawImage(
        angleImages.left.img,
        that.pos.angles.left - angleImages.width / 2,
        that.pos.legend.startY - angleImages.height,
        angleImages.width,
        angleImages.height
      );
    });

    angleImages.left.img.src = angleImages.left.imgSrc;

    // right
    this.ctx.globalCompositeOperation = 'destination-over';

    angleImages.right.img.addEventListener('load', () => {
      that.ctx!.save();
      that.ctx!.scale(-1, 1);
      that.ctx!.drawImage(
        angleImages.right.img,
        (that.pos.angles.right + angleImages.width / 2) * -1,
        that.pos.legend.startY - angleImages.height,
        angleImages.width,
        angleImages.height
      );
      that.ctx!.restore();
    });

    angleImages.right.img.src = angleImages.right.imgSrc;
  }

  clean() {
    this.canvas.style.letterSpacing = '0px';
    // Store the current transformation matrix
    this.ctx.save();

    // Use the identity matrix while clearing the canvas
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Restore the transform
    this.ctx.restore();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
